import React, { useState, useEffect } from 'react';
import { Printer, Search, Loader2, Calendar, Paperclip, Receipt } from 'lucide-react';
import { pdfjs } from 'react-pdf';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { buildDotacjaHtml } from '../components/DotacjaDisplay';

// Worker zgodny z react-pdf (5.4.296)
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const getFirstDayOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
};

const getLastDayOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
};

const Prints = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [generatingId, setGeneratingId] = useState(null);
  const [totalElements, setTotalElements] = useState(0);
  const [dateFromFilter, setDateFromFilter] = useState(getFirstDayOfMonth);
  const [dateToFilter, setDateToFilter] = useState(getLastDayOfMonth);

  useEffect(() => {
    fetchInvoices();
  }, [dateFromFilter, dateToFilter]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = {
        page: 0,
        size: 500,
        sort: 'createdAt',
        direction: 'desc',
        forPrints: true
      };
      if (dateFromFilter) params.dateFrom = dateFromFilter;
      if (dateToFilter) params.dateTo = dateToFilter;
      if (searchQuery.trim()) params.q = searchQuery.trim();
      const response = await api.get('/invoices', { params });
      const content = response.data?.content ?? response.data;
      const list = Array.isArray(content) ? content : [];
      setInvoices(list);
      setTotalElements(response.data?.totalElements ?? list.length);
    } catch (error) {
      console.error('Błąd pobierania faktur:', error);
      toast.error('Nie udało się załadować listy faktur');
      setInvoices([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchInvoices();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pl-PL');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status) => {
    const labels = {
      'NEW': 'Nowa',
      'IN_PROGRESS': 'W realizacji',
      'ASSIGNED': 'Przekazana',
      'RETURNED': 'Zwrócona',
      'PENDING': 'Oczekujące',
      'DONE': 'Zaksięgowana',
      'SIGNED': 'Podpisana'
    };
    return labels[status] || status || '-';
  };

  const getStatusColor = (status) => {
    const colors = {
      'NEW': 'bg-green-600 text-white',
      'IN_PROGRESS': 'bg-orange-500 text-white',
      'ASSIGNED': 'bg-yellow-500 text-white',
      'RETURNED': 'bg-orange-50 text-orange-700 border border-orange-200',
      'PENDING': 'bg-gray-100 text-gray-700',
      'DONE': 'bg-green-50 text-green-700 border border-green-200',
      'SIGNED': 'bg-blue-50 text-blue-700 border border-blue-200'
    };
    return colors[status] || 'bg-gray-50 text-gray-700 border border-gray-200';
  };

  /** Prefiksuje selektory CSS, aby style faktury KSeF nie wyciekały na cały wydruk. */
  const scopeCss = (css, scope) => {
    if (!css) return '';
    return css.replace(/(^|\})\s*([^{}]+?)\s*\{/g, (match, brace, selector) => {
      const scoped = selector
        .split(',')
        .map((sel) => {
          const s = sel.trim();
          if (!s) return s;
          if (s === 'body' || s === 'html') return scope;
          if (s === '*') return `${scope} *`;
          return `${scope} ${s}`;
        })
        .join(', ');
      return `${brace} ${scoped} {`;
    });
  };

  /** Wyciąga style oraz treść faktury z HTML podglądu (bez zagnieżdżonego dokumentu i podpisów). */
  const extractPrintableInvoice = (html) => {
    if (!html || typeof html !== 'string') return { styles: '', body: '' };

    const styleMatches = html.match(/<style[^>]*>([\s\S]*?)<\/style>/gi) || [];
    const rawStyles = styleMatches
      .map((block) => block.replace(/<\/?style[^>]*>/gi, ''))
      .join('\n');
    const styles = scopeCss(rawStyles, '.ksef-invoice');

    let content = html;
    const bodyMatch = content.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    if (bodyMatch) content = bodyMatch[1];
    const sigMarker = content.search(/Podpisy elektroniczne/i);
    if (sigMarker > -1) {
      const divStart = content.lastIndexOf('<div', sigMarker);
      if (divStart > -1) {
        content = content.substring(0, divStart);
      }
    }
    content = content.replace(/<div[^>]*id=["']crm-visual-stamps["'][^>]*>[\s\S]*?<\/div>/gi, '');
    content = content.replace(/<div[^>]*style=["'][^"']*position:\s*fixed[^"']*["'][^>]*>[\s\S]*?<\/div>/gi, '');
    content = content.replace(/<div[^>]*>[\s\S]*?podpisany przez[\s\S]*?<\/div>/gi, (block) => {
      if (/position:\s*fixed|bottom:\s*\d|z-index:\s*99999/i.test(block)) return '';
      return block;
    });
    return { styles, body: content.trim() };
  };

  /** Pobiera logo szpitala jako data URL (żeby było dostępne w iframe druku). */
  const loadLogoDataUrl = async () => {
    try {
      const resp = await fetch(`${window.location.origin}/images/cropped-logo-szpital_akredytacja.png`);
      if (!resp.ok) return '';
      const blob = await resp.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => resolve('');
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      return '';
    }
  };

  const buildCombinedSignatureItems = (signatures, descriptions) => {
    const activeSignatures = (signatures || []).filter((sig) => sig.crossedOut !== true);
    const combinedItems = [];
    const matchedSigIds = new Set();

    descriptions.forEach((desc) => {
      if (desc.signatureCrossedOut || desc.requiresResignature) {
        // Opis ze skreślonym podpisem – na wydruku pomijamy (wymaga ponownego podpisu)
        return;
      }
      const signature = activeSignatures.find((sig) => {
        if (desc.documentSignatureId && sig.id && String(desc.documentSignatureId) === String(sig.id)) return true;
        if (desc.userId && sig.user?.id && String(desc.userId) === String(sig.user.id) &&
            Math.abs(new Date(desc.createdAt) - new Date(sig.signedAt)) < 60000) return true;
        return false;
      });
      if (signature?.id) matchedSigIds.add(signature.id);
      if (!signature) {
        return;
      }
      const timestamp = signature ? new Date(signature.signedAt) : new Date(desc.createdAt);
      combinedItems.push({ signature, description: desc, timestamp });
    });

    activeSignatures.forEach((sig) => {
      if (sig.id && !matchedSigIds.has(sig.id)) {
        combinedItems.push({ signature: sig, description: null, timestamp: new Date(sig.signedAt) });
      }
    });

    combinedItems.sort((a, b) => a.timestamp - b.timestamp);
    return combinedItems;
  };

  const buildSignatureBlockHtml = (sig, desc, idx) => {
    const userName = sig?.user ? `${sig.user.firstName || ''} ${sig.user.lastName || ''}`.trim() || sig.user?.email : (desc?.userName || desc?.userEmail || 'nieznany użytkownik');
    const ts = sig?.signedAt ? new Date(sig.signedAt) : (desc?.createdAt ? new Date(desc.createdAt) : null);
    const dateShort = ts ? ts.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
    const timeShort = ts ? ts.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' }) : '';

    let mainLine = '';
    if (desc?.metricalChecked) {
      mainLine = `SPRAWDZONO POD WZGLĘDEM MERYTORYCZNYM przez ${userName}, ${dateShort}, ${timeShort} - podpisano elektronicznie`;
    } else if (desc?.preliminaryCheckDone) {
      mainLine = `DOKONANO WSTĘPNEJ KONTROLI przez ${userName}, ${dateShort}, ${timeShort} - podpisano elektronicznie`;
    } else if (desc?.formalnoRachunkowoChecked) {
      mainLine = `SPRAWDZONO POD WZGLĘDEM FORMALNO-RACHUNKOWYM przez ${userName}, ${dateShort}, ${timeShort} - podpisano elektronicznie`;
    } else if (desc?.zatwierdzonoDo === 'REALIZACJI') {
      mainLine = `ZATWIERDZONO DO REALIZACJI przez ${userName}, ${dateShort}, ${timeShort} - podpisano elektronicznie`;
    } else if (sig?.attachmentFileName) {
      mainLine = `Podpisano załącznik „${sig.attachmentFileName}” przez ${userName}, ${dateShort}, ${timeShort} - podpisano elektronicznie`;
    } else if (desc && !sig) {
      mainLine = `Opis przez ${userName}, ${dateShort}, ${timeShort}`;
    } else {
      mainLine = `Podpisano przez ${userName}, ${dateShort}, ${timeShort} - podpisano elektronicznie`;
    }

    let extraHtml = '';
    if (desc?.zatwierdzonoDo === 'REALIZACJI') {
      extraHtml += '<div class="sig-extra sig-approved">✓ ZATWIERDZONO DO REALIZACJI</div>';
    }
    if (desc?.formalnoRachunkowoChecked) {
      extraHtml += '<div class="sig-extra">';
      if (desc.zatwierdzonoDo && desc.zatwierdzonoDo !== 'REALIZACJI') {
        extraHtml += `<div class="sig-extra-line">ZATWIERDZONO DO: <strong>${desc.zatwierdzonoDo}</strong></div>`;
      }
      if (desc.kwota) {
        extraHtml += `<div class="sig-extra-line">KWOTĘ: <strong>${desc.kwota} PLN</strong>${desc.kwotaSlownie ? ` (${desc.kwotaSlownie})` : ''}</div>`;
      }
      extraHtml += '</div>';
    }
    if (desc?.invoicePaymentType === 'CASH' || desc?.invoicePaymentType === 'TRANSFER') {
      extraHtml += `<div class="sig-extra">Typ płatności: <strong>${desc.invoicePaymentType === 'CASH' ? 'Faktura gotówkowa' : 'Przelew'}</strong></div>`;
    }
    if (desc?.description) {
      extraHtml += `<div class="sig-extra">Opis: ${desc.description}${desc.costCenter ? `<span class="sig-cost-center">Ośrodek kosztów: ${desc.costCenter}</span>` : ''}</div>`;
    }
    if (desc?.signedByPosition) {
      extraHtml += `<div class="sig-extra">Stanowisko: ${desc.signedByPosition}</div>`;
    }
    extraHtml += buildDotacjaHtml(desc);

    return `
      <div class="sig-block-pro">
        <div class="sig-block-inner">
          <div class="sig-num">${idx + 1}.</div>
          <div class="sig-body">
            <div class="sig-main">${mainLine}</div>
            ${extraHtml}
          </div>
        </div>
      </div>
    `;
  };

  const buildPrintContent = async (invoice) => {
    let invoicePage1 = '';
    let invoiceStyles = '';
    let isPdf = false;
    let pdfPageImages = [];

    const logoDataUrl = await loadLogoDataUrl();

    const renderPdfBlobToImages = async (blob) => {
      const arrayBuffer = await blob.arrayBuffer();
      const pdfDoc = await pdfjs.getDocument({ data: arrayBuffer }).promise;
      const images = [];
      const scale = 2;
      for (let i = 1; i <= pdfDoc.numPages; i++) {
        const page = await pdfDoc.getPage(i);
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        await page.render({ canvasContext: ctx, viewport }).promise;
        images.push(canvas.toDataURL('image/png'));
      }
      return images;
    };

    // Faktury KSeF/PDF: czysty plik z backendu (bez stempli). HTML tylko jako zapas.
    const preferPrintPdf = !!(
      invoice.ksefReference ||
      /\.(xml|pdf)$/i.test(invoice.filePath || '')
    );

    if (preferPrintPdf) {
      try {
        const fileRes = await api.get(`/invoices/${invoice.id}/file`, {
          params: { forPrint: true },
          responseType: 'blob'
        });
        pdfPageImages = await renderPdfBlobToImages(fileRes.data);
        if (pdfPageImages.length > 0) {
          isPdf = true;
        }
      } catch (pdfErr) {
        console.warn('Wydruk: nie udało się pobrać czystego PDF, próba HTML:', pdfErr);
      }
    }

    if (!isPdf) {
      try {
        const previewRes = await api.get(`/invoices/${invoice.id}/preview`, {
          params: { includeSignatures: false, forPrint: true },
          responseType: 'text'
        });
        const { styles, body } = extractPrintableInvoice(previewRes.data);
        invoiceStyles = styles;
        invoicePage1 = `<div class="invoice-content ksef-invoice">${body}</div>`;
      } catch (previewErr) {
        if (!preferPrintPdf) {
          try {
            const fileRes = await api.get(`/invoices/${invoice.id}/file`, {
              params: { forPrint: true },
              responseType: 'blob'
            });
            pdfPageImages = await renderPdfBlobToImages(fileRes.data);
            isPdf = pdfPageImages.length > 0;
          } catch (fileErr) {
            console.error('PDF render error:', fileErr);
            throw new Error('Nie udało się pobrać lub przetworzyć faktury');
          }
        } else {
          console.error('HTML/PDF render error:', previewErr);
          throw new Error('Nie udało się pobrać lub przetworzyć faktury');
        }
      }
    }

    const attachmentPageImages = [];
    try {
      const attRes = await api.get(`/invoices/${invoice.id}/attachments`).catch(() => ({ data: [] }));
      const attachments = Array.isArray(attRes.data) ? attRes.data : [];
      for (const att of attachments) {
        const fileType = (att.fileType || '').toLowerCase();
        try {
          const downloadRes = await api.get(`/invoices/attachments/${att.id}/download`, {
            params: { forPrint: true },
            responseType: 'blob'
          });
          const blob = downloadRes.data;
          const isPdfType = fileType === 'application/pdf' || (blob.type && blob.type === 'application/pdf');
          const isImageType = (fileType && fileType.startsWith('image/')) || (blob.type && blob.type.startsWith('image/'));
          if (isPdfType) {
            const imgs = await renderPdfBlobToImages(blob);
            attachmentPageImages.push(...imgs);
          } else if (isImageType) {
            const reader = new FileReader();
            const dataUrl = await new Promise((resolve, reject) => {
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
            attachmentPageImages.push(dataUrl);
          }
        } catch (attErr) {
          console.warn('Nie udało się załadować załącznika', att.id, attErr);
        }
      }
    } catch (attErr) {
      console.warn('Błąd pobierania załączników:', attErr);
    }

    const [sigRes, descRes] = await Promise.all([
      api.get(`/documents/invoices/${invoice.id}/signatures`).catch(() => ({ data: [] })),
      api.get(`/invoices/${invoice.id}/descriptions`).catch(() => ({ data: [] }))
    ]);
    const signatures = Array.isArray(sigRes.data) ? sigRes.data : [];
    const descriptions = Array.isArray(descRes.data) ? descRes.data : [];

    const combinedItems = buildCombinedSignatureItems(signatures, descriptions);

    const signaturesHtml = combinedItems.length > 0
      ? `<div class="sig-list">${combinedItems
        .map((item, idx) => buildSignatureBlockHtml(item.signature, item.description, idx))
        .join('')}</div>`
      : '<p class="sig-empty">Brak opisów i podpisów dla tej faktury.</p>';

    const invoiceNumber = invoice.number || invoice.internalNumber || '-';

    const pdfPagesHtml = pdfPageImages.map((dataUrl) =>
      `<div class="pdf-page"><img src="${dataUrl}" alt="" class="pdf-page-img" /></div>`
    ).join('');

    const attachmentsHtml = attachmentPageImages.length > 0
      ? `<section class="print-section print-attachments print-major-break">${attachmentPageImages.map((dataUrl) =>
        `<div class="pdf-page"><img src="${dataUrl}" alt="" class="pdf-page-img" /></div>`
      ).join('')}</section>`
      : '';

    const invoiceSection = isPdf
      ? `<section class="print-section print-invoice-pdf">${pdfPagesHtml}</section>`
      : `<section class="print-section print-invoice-xml">${invoicePage1}</section>`;

    const docHeaderHtml = isPdf
      ? ''
      : `<div class="print-doc-header">
          <div class="doc-header-text">
            <div class="doc-header-title">Faktura ${invoiceNumber}</div>
            ${invoice.supplierName ? `<div>${invoice.supplierName}${invoice.supplierNip ? ` — NIP ${invoice.supplierNip}` : ''}</div>` : ''}
          </div>
          ${logoDataUrl ? `<img class="doc-logo" src="${logoDataUrl}" alt="Szpital Łapy" />` : ''}
        </div>`;

    const printStyles = `
      * { margin: 0; padding: 0; box-sizing: border-box; }
      html, body { width: 100%; background: #fff; }
      body {
        font-family: 'Times New Roman', Times, serif;
        font-size: 10pt;
        line-height: 1.4;
        color: #000;
        padding: 0;
      }
      .print-doc-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        font-size: 9pt;
        color: #333;
        border-bottom: 2px solid #000;
        padding-bottom: 6px;
        margin-bottom: 10px;
      }
      .doc-header-text { flex: 1; min-width: 0; }
      .doc-header-title { font-size: 11pt; font-weight: bold; color: #000; }
      .doc-logo { height: 46px; width: auto; flex-shrink: 0; object-fit: contain; }
      .print-section { margin-bottom: 0; }
      .print-major-break { break-before: page; page-break-before: always; }
      .print-signatures.print-major-break > :first-child { break-before: avoid; page-break-before: avoid; }
      .section-title {
        font-size: 11pt;
        font-weight: bold;
        margin: 0 0 8px 0;
        padding-bottom: 4px;
        border-bottom: 1px solid #000;
      }
      .doc-main-title { font-size: 12pt; }
      .invoice-content, .ksef-invoice { width: 100%; overflow: visible; }
      .ksef-invoice .invoice-container { max-width: 100% !important; margin: 0 !important; padding: 0 !important; }
      .ksef-invoice body, .ksef-invoice { padding: 0 !important; font-size: 9pt !important; line-height: 1.35 !important; }
      .ksef-invoice table { width: 100%; border-collapse: collapse; font-size: 7.5pt; page-break-inside: auto; }
      .ksef-invoice thead { display: table-header-group; }
      .ksef-invoice tr { page-break-inside: avoid; page-break-after: auto; }
      .ksef-invoice th, .ksef-invoice td { padding: 3px 4px; border: 1px solid #000; }
      .ksef-invoice .parties, .ksef-invoice .details-section, .ksef-invoice .invoice-header {
        page-break-inside: avoid;
        margin-bottom: 8px;
      }
      .ksef-invoice img { max-width: 100%; height: auto; }
      .ksef-invoice #crm-visual-stamps,
      .ksef-invoice [id*="visual-stamp"],
      .ksef-invoice [style*="position:fixed"],
      .ksef-invoice [style*="position: fixed"] {
        display: none !important;
        visibility: hidden !important;
        height: 0 !important;
        overflow: hidden !important;
      }
      .pdf-page {
        width: 100%;
        margin: 0;
        padding: 0;
        text-align: center;
        line-height: 0;
        break-inside: avoid;
        page-break-inside: avoid;
      }
      .print-invoice-pdf .pdf-page + .pdf-page,
      .print-attachments .pdf-page + .pdf-page {
        break-before: page;
        page-break-before: always;
      }
      .print-major-break > .pdf-page:first-child {
        break-before: avoid;
        page-break-before: avoid;
      }
      .pdf-page-img {
        width: auto;
        max-width: 100%;
        max-height: 250mm;
        height: auto;
        display: block;
        margin: 0 auto;
        object-fit: contain;
        break-inside: avoid;
        page-break-inside: avoid;
      }
      .print-signatures { padding-top: 4mm; }
      .sig-intro { font-size: 9pt; color: #333; margin-bottom: 10px; }
      .sig-list { display: block; }
      .sig-block-pro {
        border: 1px solid #999;
        border-left: 3px solid #000;
        padding: 5px 8px;
        margin-bottom: 6px;
        background: #fff;
        page-break-inside: avoid;
        font-size: 9pt;
        line-height: 1.35;
      }
      .sig-block-inner { display: table; width: 100%; }
      .sig-num {
        display: table-cell;
        vertical-align: top;
        width: 22px;
        font-weight: bold;
        font-size: 9pt;
        padding-right: 6px;
      }
      .sig-body { display: table-cell; vertical-align: top; }
      .sig-main { font-size: 9pt; color: #000; margin-bottom: 2px; }
      .sig-extra { margin-top: 4px; padding-top: 4px; border-top: 1px dotted #999; font-size: 8pt; color: #222; }
      .sig-extra-line { margin-bottom: 2px; }
      .sig-approved { font-weight: 600; }
      .sig-cost-center { display: block; margin-top: 2px; font-weight: 600; }
      .sig-empty { font-size: 9pt; color: #444; font-style: italic; }
      .sig-meta-row {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 8px;
        font-size: 9pt;
        margin-bottom: 10px;
        padding-bottom: 6px;
        border-bottom: 1px solid #ccc;
      }
      @media print {
        @page { size: A4 portrait; margin: 12mm 10mm; }
        html, body {
          background: white !important;
          overflow: visible !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .print-major-break { break-before: page; page-break-before: always; }
        .print-major-break > .pdf-page:first-child,
        .print-signatures.print-major-break > :first-child {
          break-before: avoid;
          page-break-before: avoid;
        }
        .sig-block-pro { page-break-inside: avoid; }
        .ksef-invoice table { font-size: 7pt; }
        .ksef-invoice #crm-visual-stamps,
        .ksef-invoice [style*="position:fixed"],
        .ksef-invoice [style*="position: fixed"] {
          display: none !important;
        }
      }
    `;

    return `
      <!DOCTYPE html>
      <html lang="pl">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Wydruk faktury ${invoice.number || invoice.id}</title>
        <style>${invoiceStyles}</style>
        <style>${printStyles}</style>
      </head>
      <body>
        ${docHeaderHtml}
        ${invoiceSection}
        ${attachmentsHtml}
        <section class="print-section print-signatures print-major-break">
          <h2 class="section-title">Historia podpisów elektronicznych</h2>
          <div class="sig-meta-row">
            <span><strong>Faktura:</strong> ${invoiceNumber}</span>
            ${invoice.supplierName ? `<span><strong>Dostawca:</strong> ${invoice.supplierName}</span>` : ''}
          </div>
          <p class="sig-intro">Poniżej lista akceptacji i podpisów wewnętrznych w systemie CRM (kolejność chronologiczna).</p>
          ${signaturesHtml}
        </section>
      </body>
      </html>
    `;
  };

  const handleGeneratePrint = async (invoice) => {
    if (!invoice?.id) return;
    setGeneratingId(invoice.id);
    try {
      const htmlContent = await buildPrintContent(invoice);
      const iframe = document.createElement('iframe');
      iframe.name = 'print-frame';
      iframe.style.cssText = 'position:fixed;left:-9999px;top:0;width:210mm;min-height:297mm;border:none;';
      document.body.appendChild(iframe);
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(htmlContent);
      doc.close();
      const doPrint = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.onafterprint = () => document.body.removeChild(iframe);
          iframe.contentWindow.print();
          toast.success('Otwieram druk. W oknie druku wyłącz „Nagłówki i stopki”, aby ukryć adres URL.', { duration: 6000 });
        } catch (e) {
          document.body.removeChild(iframe);
          toast.error('Nie udało się otworzyć drukowania.');
        }
      };
      iframe.onload = () => setTimeout(doPrint, 400);
    } catch (err) {
      console.error('Błąd generowania wydruku:', err);
      toast.error(err.message || 'Nie udało się wygenerować dokumentu do druku');
    } finally {
      setGeneratingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Printer className="h-8 w-8 text-primary-600" />
          Wydruki
        </h1>
        <p className="mt-1 text-gray-600">
          Wszystkie faktury z systemu – generuj dokument do druku: oryginał faktury oraz historia podpisów z treścią.
        </p>
        {invoices.length > 0 && (
          <p className="mt-1 text-sm text-gray-600">
            Znaleziono {totalElements} {totalElements === 1 ? 'fakturę' : totalElements < 5 ? 'faktury' : 'faktur'}
            {searchQuery ? ` (wyszukiwanie: "${searchQuery}")` : ''}
          </p>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <form onSubmit={handleSearch} className="p-4 border-b border-gray-200 flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2">
            <label className="text-xs sm:text-sm text-gray-700 whitespace-nowrap hidden sm:inline">Data dodania:</label>
            <div className="relative">
              <Calendar className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type="date"
                value={dateFromFilter}
                onChange={(e) => setDateFromFilter(e.target.value)}
                className="pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <span className="text-gray-500">-</span>
            <div className="relative">
              <Calendar className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type="date"
                value={dateToFilter}
                onChange={(e) => setDateToFilter(e.target.value)}
                className="pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div className="flex-1 min-w-[200px] flex items-center border border-gray-300 rounded-lg overflow-hidden">
            <Search className="h-4 w-4 text-gray-500 ml-3" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Szukaj po numerze, dostawcy, NIP..."
              className="flex-1 px-3 py-2 border-0 focus:ring-0 focus:outline-none text-sm"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium">
            Szukaj
          </button>
        </form>

        {loading ? (
          <div className="p-12 flex justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Numer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dostawca</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data dodania</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kwota</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Akcje</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                      Brak faktur do wyświetlenia.
                    </td>
                  </tr>
                ) : (
                  invoices.map((inv) => (
                    <tr key={inv.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        <div className="flex items-center gap-2">
                          <span>{inv.number || inv.internalNumber || '-'}</span>
                          {inv.ksefReference && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold text-green-700 bg-green-100" title={`Faktura z KSeF: ${inv.ksefReference}`}>
                              <Receipt className="h-3 w-3" />
                              KSeF
                            </span>
                          )}
                          {(inv.attachmentCount ?? 0) > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-green-600" title={`Załączniki (${inv.attachmentCount})`}>
                              <Paperclip className="h-4 w-4" />
                              <span className="text-[10px] font-medium text-green-700">{inv.attachmentCount}</span>
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{inv.supplierName || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatDate(inv.issueDate)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{formatDateTime(inv.createdAt)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {inv.amountGross != null ? `${Number(inv.amountGross).toFixed(2)} ${inv.currency || 'PLN'}` : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(inv.status)}`}>
                          {getStatusLabel(inv.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleGeneratePrint(inv)}
                            disabled={generatingId === inv.id || (!inv.filePath && !inv.ksefReference)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={!inv.filePath && !inv.ksefReference ? 'Brak pliku faktury' : 'Generuj dokument do druku lub PDF (Ctrl+P, w oknie druku wybierz „Zapisz jako PDF”)'}
                          >
                            {generatingId === inv.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Printer className="h-4 w-4" />
                            )}
                            Generuj Wydruk/PDF
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Prints;
